import { cn } from '@mdxui/primitives/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@mdxui/primitives'
import { formatDate } from '../lib/utils'
import type { ComponentType, ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { type BlogPost, BlogPostHeader, ReadMoreSection, TableOfContents } from '../components'
import { BlogPostSEO, type BlogSEOConfig, type BlogPostFAQItem, type HowToData } from '../seo'
import { DisplayAd, type DisplayAdProps } from '../shared/display-ad'
import { HashScrollHandler } from '../shared/hash-scroll-handler'
import { MarkdownContent } from '../shared/markdown-content'

import remarkGfm from "remark-gfm"
import rehypeSlug from "rehype-slug"
import { markdownTableComponents } from '../shared'

export interface BlogPostPageLinkProps {
  href: string
  children: ReactNode
  className?: string
}

/** Props for custom TOC components */
export interface TocComponentProps {
  author?: { name: string; image?: string }
  className?: string
}

export interface BlogPostPageProps {
  /** Blog post data */
  post: BlogPost
  /** Post content (rendered markdown/MDX) */
  children?: ReactNode
  /** Show sidebar with author and TOC (default: true) */
  showSidebar?: boolean
  /** Show table of contents in sidebar (default: true) */
  showToc?: boolean
  /** Show read more section at bottom (default: true) */
  showReadMore?: boolean
  /** Related posts for read more section */
  relatedPosts?: BlogPost[]
  /** Optional display ad configuration */
  displayAd?: DisplayAdProps
  /** SEO configuration - enables structured data when provided */
  seoConfig?: BlogSEOConfig
  /** FAQ items - renders FAQ section and adds FAQPage schema when provided */
  faq?: BlogPostFAQItem[]
  /** HowTo data - adds HowTo schema when provided */
  howTo?: HowToData
  /** Custom link component */
  LinkComponent?: ComponentType<BlogPostPageLinkProps>
  /** Base path for post links (default: '') — e.g. '/blog' produces '/blog/{slug}' */
  basePath?: string
  /** Custom Table of Contents component (e.g., TanStack-specific TOC) */
  TocComponent?: ComponentType<TocComponentProps>
  /** Additional className */
  className?: string
}

/**
 * Blog post page component
 *
 * @example
 * ```tsx
 * <BlogPostPage
 *   post={{
 *     slug: 'hello-world',
 *     title: 'Hello World',
 *     description: 'My first blog post',
 *     date: '2024-01-01',
 *     author: { name: 'John Doe', image: '/john.jpg' },
 *     tags: ['news'],
 *   }}
 *   showSidebar
 *   showToc
 * >
 *   <p>Post content here...</p>
 * </BlogPostPage>
 * ```
 */
export function BlogPostPage({
  post,
  children,
  showSidebar = true,
  showToc = true,
  showReadMore = true,
  relatedPosts = [],
  displayAd,
  seoConfig,
  faq,
  howTo,
  LinkComponent,
  basePath = '',
  TocComponent,
  className,
}: BlogPostPageProps) {
  // Use custom TOC component if provided, otherwise default to internal TableOfContents
  const TocImpl = TocComponent ?? TableOfContents
  const formattedDate = post.date ? formatDate(post.date) : undefined

  return (
    <main className={cn('min-h-screen flex flex-col flex-1 relative bg-background', className)}>
      {/* SEO structured data */}
      {seoConfig && (
        <BlogPostSEO
          post={{
            slug: post.slug,
            title: post.title,
            description: post.description,
            date: post.date,
            author: post.author ? { name: post.author.name, image: post.author.image } : undefined,
            tags: post.tags,
            image: post.image,
          }}
          config={seoConfig}
          faq={faq}
          howTo={howTo}
        />
      )}
      <HashScrollHandler />

      <div className='flex-1'>
        <BlogPostHeader
          title={post.title}
          description={post.description}
          tags={post.tags}
          date={formattedDate}
          LinkComponent={LinkComponent}
        />

        <div className='flex lg:divide-x lg:divide-border relative max-w-7xl mx-auto'>
          {/* Decorative border - hidden on mobile */}
          <div className='hidden lg:block absolute max-w-7xl mx-auto left-1/2 -translate-x-1/2 w-full h-full border-x border-border pointer-events-none' />

          {/* Main content */}
          <article className='w-full pl-0 lg:pl-px overflow-hidden'>
            {/* Post content */}
            <div className='p-6 lg:p-8 overflow-hidden'>
              <MarkdownContent>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSlug]}
                  components={markdownTableComponents}
                >
                  {children as string}
                </ReactMarkdown>
              </MarkdownContent>

              {/* FAQ section */}
              {faq && faq.length > 0 && (
                <BlogPostFAQ items={faq} className='mt-12 pt-8 border-t border-dashed border-border' />
              )}

              {/* Mobile display ad */}
              {displayAd && (
                <div className='mt-12 lg:hidden'>
                  <DisplayAd {...displayAd} LinkComponent={LinkComponent} className='w-full' />
                </div>
              )}
            </div>
          </article>

          {/* Sidebar */}
          {showSidebar && (
            <aside className='hidden lg:block relative w-[350px] shrink-0 self-stretch bg-muted/60 dark:bg-muted/20'>
              <div className='sticky top-16 p-6 lg:p-8 space-y-6'>
                <div className='border border-border rounded-[var(--radius)] p-6 bg-background'>
                  {showToc && (
                    <TocImpl
                      author={post.author}
                    />
                  )}
                </div>

                {displayAd && <DisplayAd {...displayAd} LinkComponent={LinkComponent} />}
              </div>
            </aside>
          )}
        </div>

        {/* Read more section */}
        {showReadMore && relatedPosts.length > 0 && (
          <ReadMoreSection posts={relatedPosts} currentSlug={post.slug} currentTags={post.tags} LinkComponent={LinkComponent} basePath={basePath} />
        )}
      </div>
    </main>
  )
}

/**
 * BlogPostFAQ - Inline FAQ section for blog posts
 *
 * A simple accordion-based FAQ component designed to fit within blog post flow.
 * Uses the same FAQItem type as the SEO schema for consistency.
 */
interface BlogPostFAQProps {
  items: BlogPostFAQItem[]
  /** Section title (default: 'Frequently Asked Questions') */
  title?: string
  className?: string
}

function BlogPostFAQ({
  items,
  title = 'Frequently Asked Questions',
  className,
}: BlogPostFAQProps) {
  return (
    <section className={cn(className)}>
      <h2 className="text-2xl font-semibold text-foreground mb-6">{title}</h2>
      <Accordion type="single" collapsible>
        {items.map((item, index) => (
          <AccordionItem key={`faq-${index.toString()}`} value={`faq-${index}`}>
            <AccordionTrigger className="text-left font-medium hover:no-underline">
              {item.question}
            </AccordionTrigger>
            <AccordionContent>
              {item.answerContent ? (
                <MarkdownContent className="text-muted-foreground prose-sm">
                  {item.answerContent}
                </MarkdownContent>
              ) : (
                <div className="prose prose-sm max-w-none text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{item.answer}</ReactMarkdown>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  )
}
